import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import Table from 'react-bootstrap/Table';
import i18nContext from 'components/i18n-context';
import { formatDateTime, getCustomerLink } from 'services/utils';
import { getCustomerInfoRequest } from 'services/requestAgent';
import { ROUTE_PATHS } from 'routes/constants';
import Button from 'uikit/Button/Button';
import NotesTab from 'components/NotesTab';
import './MonitoringCheckTab.scss';

const MonitoringCheckTab = ({
  isLoading,
  transactionData,
  transactionNumber,
  notes,
  counterpartyVerification,
  paymentLimits,
  sanctions,
  blacklistDetails,
  ruleEngineScoring,
  handleLoadNotes,
  handleAddNote,
  handleDeleteNote,
  handleDownloadDocument
}) => {
  const i18n = useContext(i18nContext);
  const navigate = useNavigate();

  const handleRedirectToLimits = async (group) => {
    let redirectUrl = ROUTE_PATHS.LIMITS;

    if (group === 'individual' && transactionData) {
      const customerInfo = await getCustomerInfoRequest(transactionData.accountNumber);
      redirectUrl = getCustomerLink(transactionData.accountNumber, customerInfo.status, customerInfo.type);
    }
    navigate(redirectUrl);
  };

  if (!transactionData) return null;

  return (
    <div className={'monitoring-check-tab-wrapper'}>
      <div className={'monitoring-check-tab-content'}>
        <p className={'rule-engine-header'}>{i18n.getMessage('transactionDetails.counterpartyVerification')}</p>
        {counterpartyVerification ? (
          <div className={'transaction-details-tables-wrapper'}>
            <table className={'table-wrapper'}>
              <tbody className={'table-body'}>
                <tr className={'table-row'}>
                  <td className={'table-cell row-label'}>
                    {i18n.getMessage('transactionDetails.counterpartyVerification.field.status')}
                  </td>
                  <td className={'table-cell'}>{counterpartyVerification?.status}</td>
                </tr>

                <tr className={'table-row'}>
                  <td className={'table-cell row-label'}>
                    {i18n.getMessage('transactionDetails.counterpartyVerification.field.createdAt')}
                  </td>
                  <td className={'table-cell'}>{formatDateTime(counterpartyVerification?.createdAt)}</td>
                </tr>

                <tr className={'table-row'}>
                  <td className={'table-cell row-label'}>
                    {i18n.getMessage('transactionDetails.counterpartyVerification.field.updatedAt')}
                  </td>
                  <td className={'table-cell'}>{formatDateTime(counterpartyVerification?.updatedAt)}</td>
                </tr>
              </tbody>
            </table>

            <table className={'table-wrapper'}>
              <tbody className={'table-body'}>
                <tr className={'table-row'}>
                  <td className={'table-cell row-label'}>
                    {i18n.getMessage('transactionDetails.counterpartyVerification.field.action')}
                  </td>
                  <td className={'table-cell'}>{counterpartyVerification?.action}</td>
                </tr>

                <tr className={'table-row'}>
                  <td className={'table-cell row-label'}>
                    {i18n.getMessage('transactionDetails.counterpartyVerification.field.details')}
                  </td>
                  <td className={'table-cell'}>{counterpartyVerification?.details}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className={'transaction-details-tables-wrapper'}>
            <table className={'table-wrapper'}>
              <tbody className={'table-body'}>
                <tr className={'table-row'}>
                  <td className={'table-cell empty-rule-engine-cell'}>
                    {i18n.getMessage('transactionDetails.counterpartyVerification.notFound')}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <p className={'rule-engine-header'}>{i18n.getMessage('transactionDetails.limits')}</p>
        {paymentLimits ? (
          <div className={'transaction-details-payment-limits-wrapper'}>
            <div className={'transaction-details-tables-wrapper'}>
              <table className={'table-wrapper'}>
                <tbody className={'table-body'}>
                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>{i18n.getMessage('transactionDetails.limits.status')}</td>
                    <td className={'table-cell'}>{paymentLimits?.status}</td>
                  </tr>
                </tbody>
              </table>

              <table className={'table-wrapper'}>
                <tbody className={'table-body'}>
                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.ruleEngine.externalStatus')}
                    </td>
                    <td className={'table-cell'}>{formatDateTime(paymentLimits?.createdAt)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {paymentLimits?.details && (
              <Table responsive>
                <thead>
                  <tr>
                    <th className={'advanced-details-table-head'}>
                      <div className={'advanced-details-table-head-block'}>
                        <span>{i18n.getMessage('transactionDetails.limits.details.limitGroup')}</span>
                      </div>
                    </th>
                    <th className={'advanced-details-table-head'}>
                      <div className={'advanced-details-table-head-block'}>
                        <span>{i18n.getMessage('transactionDetails.limits.details.limitType')}</span>
                      </div>
                    </th>
                    <th className={'advanced-details-table-head'}>
                      <div className={'advanced-details-table-head-block'}>
                        <span>{i18n.getMessage('transactionDetails.limits.details.transferProvider')}</span>
                      </div>
                    </th>
                    <th className={'advanced-details-table-head'}>
                      <div className={'advanced-details-table-head-block'}>
                        <span>{i18n.getMessage('transactionDetails.limits.details.transactionType')}</span>
                      </div>
                    </th>
                    <th className={'advanced-details-table-head'}>
                      <div className={'advanced-details-table-head-block'}>
                        <span>{i18n.getMessage('transactionDetails.limits.details.transferType')}</span>
                      </div>
                    </th>
                    <th className={'advanced-details-table-head'}>
                      <div className={'advanced-details-table-head-block'}>
                        <span>{i18n.getMessage('transactionDetails.limits.details.period')}</span>
                      </div>
                    </th>
                    <th className={'advanced-details-table-head'}>
                      <div className={'advanced-details-table-head-block'}>
                        <span>{i18n.getMessage('transactionDetails.limits.details.currency')}</span>
                      </div>
                    </th>
                    <th className={'advanced-details-table-head'}>
                      <div className={'advanced-details-table-head-block'}>
                        <span>{i18n.getMessage('transactionDetails.limits.details.hold')}</span>
                      </div>
                    </th>
                    <th className={'advanced-details-table-head'}>
                      <div className={'advanced-details-table-head-block'}>
                        <span>{i18n.getMessage('transactionDetails.limits.details.reject')}</span>
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {JSON.parse(paymentLimits.details).map((limit, index) => (
                    <tr key={index}>
                      <td className={'advanced-details-table-cell'}>
                        <Button
                          className={'link-button advanced-details-table-cell-button'}
                          onClick={async () => await handleRedirectToLimits(limit.group)}
                          isDisabled={false}
                        >
                          {limit.group}
                        </Button>
                      </td>
                      <td className={'advanced-details-table-cell'}>
                        <p>{limit.type}</p>
                      </td>
                      <td className={'advanced-details-table-cell'}>
                        <p>{limit.transferProvider}</p>
                      </td>
                      <td className={'advanced-details-table-cell'}>
                        <p>{limit.transactionType}</p>
                      </td>
                      <td className={'advanced-details-table-cell'}>
                        <p>{limit.transferType}</p>
                      </td>
                      <td className={'advanced-details-table-cell'}>
                        <p>{limit.period}</p>
                      </td>
                      <td className={'advanced-details-table-cell'}>
                        <p>{limit.currency}</p>
                      </td>
                      <td className={'advanced-details-table-cell'}>
                        <p>{limit.holdAmount}</p>
                      </td>
                      <td className={'advanced-details-table-cell'}>
                        <p>{limit.rejectAmount}</p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        ) : (
          <div className={'transaction-details-tables-wrapper'}>
            <table className={'table-wrapper'}>
              <tbody className={'table-body'}>
                <tr className={'table-row'}>
                  <td className={'table-cell empty-rule-engine-cell'}>
                    {i18n.getMessage('transactionDetails.limits.notFound')}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <p className={'rule-engine-header'}>{i18n.getMessage('transactionDetails.amlCheckResult')}</p>
        {sanctions ? (
          <div className={'transaction-details-tables-wrapper'}>
            <table className={'table-wrapper'}>
              <tbody className={'table-body'}>
                <tr className={'table-row'}>
                  <td className={'table-cell row-label'}>
                    {i18n.getMessage('transactionDetails.amlCheckResult.field.status')}
                  </td>
                  <td className={'table-cell'}>{sanctions?.status}</td>
                </tr>

                <tr className={'table-row'}>
                  <td className={'table-cell row-label'}>
                    {i18n.getMessage('transactionDetails.amlCheckResult.field.createdAt')}
                  </td>
                  <td className={'table-cell'}>{formatDateTime(sanctions?.createdAt)}</td>
                </tr>

                <tr className={'table-row'}>
                  <td className={'table-cell row-label'}>
                    {i18n.getMessage('transactionDetails.amlCheckResult.field.updatedAt')}
                  </td>
                  <td className={'table-cell'}>{formatDateTime(sanctions?.updatedAt)}</td>
                </tr>
              </tbody>
            </table>

            <table className={'table-wrapper'}>
              <tbody className={'table-body'}>
                <tr className={'table-row'}>
                  <td className={'table-cell row-label'}>
                    {i18n.getMessage('transactionDetails.amlCheckResult.field.partyName')}
                  </td>
                  <td className={'table-cell'}>{sanctions?.partyDetails}</td>
                </tr>

                <tr className={'table-row'}>
                  <td className={'table-cell row-label'}>
                    {i18n.getMessage('transactionDetails.amlCheckResult.field.counterPartyName')}
                  </td>
                  <td className={'table-cell'}>{sanctions?.counterPartyDetails}</td>
                </tr>

                <tr className={'table-row'}>
                  <td className={'table-cell row-label'}>
                    {i18n.getMessage('transactionDetails.amlCheckResult.field.counterPartyBankName')}
                  </td>
                  <td className={'table-cell'}>{sanctions?.counterPartyBankDetails}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className={'transaction-details-tables-wrapper'}>
            <table className={'table-wrapper'}>
              <tbody className={'table-body'}>
                <tr className={'table-row'}>
                  <td className={'table-cell empty-rule-engine-cell'}>
                    {i18n.getMessage('transactionDetails.amlCheckResult.passed')}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <p className={'rule-engine-header'}>{i18n.getMessage('transactionDetails.blacklistDetails')}</p>
        {blacklistDetails ? (
          <div className={'transaction-details-tables-wrapper'}>
            <table className={'table-wrapper'}>
              <tbody className={'table-body'}>
                <tr className={'table-row'}>
                  <td className={'table-cell row-label'}>
                    {i18n.getMessage('transactionDetails.blacklistDetails.field.status')}
                  </td>
                  <td className={'table-cell'}>{blacklistDetails?.status}</td>
                </tr>

                <tr className={'table-row'}>
                  <td className={'table-cell row-label'}>
                    {i18n.getMessage('transactionDetails.blacklistDetails.field.createdAt')}
                  </td>
                  <td className={'table-cell'}>{formatDateTime(blacklistDetails?.createdAt)}</td>
                </tr>

                <tr className={'table-row'}>
                  <td className={'table-cell row-label'}>
                    {i18n.getMessage('transactionDetails.blacklistDetails.field.commentDetails')}
                  </td>
                  <td className={'table-cell'}>{blacklistDetails?.commentDetails}</td>
                </tr>
              </tbody>
            </table>

            <table className={'table-wrapper'}>
              <tbody className={'table-body'}>
                <tr className={'table-row'}>
                  <td className={'table-cell row-label'}>
                    {i18n.getMessage('transactionDetails.blacklistDetails.field.partyName')}
                  </td>
                  <td className={'table-cell'}>{blacklistDetails?.partyDetails}</td>
                </tr>

                <tr className={'table-row'}>
                  <td className={'table-cell row-label'}>
                    {i18n.getMessage('transactionDetails.blacklistDetails.field.counterPartyName')}
                  </td>
                  <td className={'table-cell'}>{blacklistDetails?.counterPartyDetails}</td>
                </tr>

                <tr className={'table-row'}>
                  <td className={'table-cell row-label'}>
                    {i18n.getMessage('transactionDetails.blacklistDetails.field.counterPartyBankName')}
                  </td>
                  <td className={'table-cell'}>{blacklistDetails?.counterPartyBankDetails}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className={'transaction-details-tables-wrapper'}>
            <table className={'table-wrapper'}>
              <tbody className={'table-body'}>
                <tr className={'table-row'}>
                  <td className={'table-cell empty-rule-engine-cell'}>
                    {i18n.getMessage('transactionDetails.blacklistDetails.notFound')}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <p className={'rule-engine-header'}>{i18n.getMessage('transactionDetails.ruleEngine')}</p>
        {ruleEngineScoring ? (
          <div className={'transaction-details-rule-engine-scoring-wrapper'}>
            <div className={'transaction-details-tables-wrapper'}>
              <table className={'table-wrapper'}>
                <tbody className={'table-body'}>
                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.ruleEngine.provider')}
                    </td>
                    <td className={'table-cell'}>{ruleEngineScoring?.provider}</td>
                  </tr>

                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.ruleEngine.status')}
                    </td>
                    <td className={'table-cell'}>{ruleEngineScoring?.status}</td>
                  </tr>

                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.ruleEngine.createdAt')}
                    </td>
                    <td className={'table-cell'}>{formatDateTime(ruleEngineScoring?.createdAt)}</td>
                  </tr>

                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.ruleEngine.updatedAt')}
                    </td>
                    <td className={'table-cell'}>{formatDateTime(ruleEngineScoring?.updatedAt)}</td>
                  </tr>
                </tbody>
              </table>

              <table className={'table-wrapper'}>
                <tbody className={'table-body'}>
                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.ruleEngine.externalStatus')}
                    </td>
                    <td className={'table-cell'}>{ruleEngineScoring?.externalStatus}</td>
                  </tr>

                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.ruleEngine.externalId')}
                    </td>
                    <td className={'table-cell'}>{ruleEngineScoring?.externalId}</td>
                  </tr>

                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.ruleEngine.externalScore')}
                    </td>
                    <td className={'table-cell'}>{ruleEngineScoring?.externalScore}</td>
                  </tr>

                  <tr className={'table-row'}>
                    <td className={'table-cell row-label'}>
                      {i18n.getMessage('transactionDetails.ruleEngine.externalLink')}
                    </td>
                    <td className={'table-cell'}>
                      {ruleEngineScoring?.externalLink && (
                        <a
                          className={'nav-link-text'}
                          href={ruleEngineScoring.externalLink}
                          target={'_blank'}
                          rel={'noreferrer'}
                        >
                          {ruleEngineScoring.externalLink}
                        </a>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {ruleEngineScoring?.details && (
              <Table responsive>
                <thead>
                  <tr>
                    <th className={'advanced-details-table-head'}>
                      <div className={'advanced-details-table-head-block'}>
                        <span>{i18n.getMessage('transactionDetails.ruleEngine.appliedRules.name')}</span>
                      </div>
                    </th>
                    <th className={'advanced-details-table-head'}>
                      <div className={'advanced-details-table-head-block'}>
                        <span>{i18n.getMessage('transactionDetails.ruleEngine.appliedRules.score')}</span>
                      </div>
                    </th>
                    <th className={'advanced-details-table-head'}>
                      <div className={'advanced-details-table-head-block'}>
                        <span>{i18n.getMessage('transactionDetails.ruleEngine.appliedRules.status')}</span>
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {JSON.parse(ruleEngineScoring.details).rules?.map((rule, index) => (
                    <tr key={index}>
                      <td className={'advanced-details-table-cell'}>
                        <p>{rule.name}</p>
                      </td>
                      <td className={'advanced-details-table-cell'}>
                        <p>{rule.score}</p>
                      </td>
                      <td className={'advanced-details-table-cell'}>
                        <p>{rule.status}</p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        ) : (
          <div className={'transaction-details-tables-wrapper'}>
            <table className={'table-wrapper'}>
              <tbody className={'table-body'}>
                <tr className={'table-row'}>
                  <td className={'table-cell empty-rule-engine-cell'}>
                    {i18n.getMessage('transactionDetails.ruleEngine.notFound')}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <NotesTab
        isLoading={isLoading}
        notes={notes}
        transactionNumber={transactionNumber}
        handleLoadNotes={handleLoadNotes}
        handleAddNote={handleAddNote}
        handleDeleteNote={handleDeleteNote}
        handleDownloadDocument={handleDownloadDocument}
      />
    </div>
  );
};

MonitoringCheckTab.propTypes = {
  isLoading: PropTypes.bool,
  transactionData: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])]),
  transactionNumber: PropTypes.string,
  notes: PropTypes.array,
  counterpartyVerification: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])]),
  paymentLimits: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])]),
  sanctions: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])]),
  blacklistDetails: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])]),
  ruleEngineScoring: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])]),
  handleLoadNotes: PropTypes.func,
  handleAddNote: PropTypes.func,
  handleDeleteNote: PropTypes.func,
  handleDownloadDocument: PropTypes.func
};

export default MonitoringCheckTab;
